import _extends from "@babel/runtime/helpers/extends";
import _typeof from "@babel/runtime/helpers/typeof";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _elementMappings;
import React, { useContext } from 'react';
import { FormattedDate } from 'react-intl-next';
import { ElementName, IconType, SmartLinkInternalTheme } from '../../../../constants';
import Icon from './icon';
import Link from './link';
import Lozenge from './lozenge';
import { FlexibleUiContext } from '../../../../state/flexible-ui-context';
import Badge from './badge';
import AvatarGroup from './avatar-group';
import Media from './media';
import Text from './text';
import { messages } from '../../../../messages';
import DateTime from './date-time';
import AtlaskitBadge from './atlaskit-badge';
var elementMappings = (_elementMappings = {}, _defineProperty(_elementMappings, ElementName.AttachmentCount, {
  component: Badge,
  props: {
    icon: IconType.Attachment
  }
}), _defineProperty(_elementMappings, ElementName.AuthorGroup, {
  component: AvatarGroup
}), _defineProperty(_elementMappings, ElementName.ChecklistProgress, {
  component: Badge,
  props: {
    icon: IconType.CheckItem
  }
}), _defineProperty(_elementMappings, ElementName.CollaboratorGroup, {
  component: AvatarGroup
}), _defineProperty(_elementMappings, ElementName.CommentCount, {
  component: Badge,
  props: {
    icon: IconType.Comment
  }
}), _defineProperty(_elementMappings, ElementName.ViewCount, {
  component: Badge,
  props: {
    icon: IconType.View
  }
}), _defineProperty(_elementMappings, ElementName.ReactCount, {
  component: Badge,
  props: {
    icon: IconType.React
  }
}), _defineProperty(_elementMappings, ElementName.VoteCount, {
  component: Badge,
  props: {
    icon: IconType.Vote
  }
}), _defineProperty(_elementMappings, ElementName.CreatedBy, {
  component: Text
}), _defineProperty(_elementMappings, ElementName.OwnedBy, {
  component: Text
}), _defineProperty(_elementMappings, ElementName.AssignedTo, {
  component: Text
}), _defineProperty(_elementMappings, ElementName.AssignedToGroup, {
  component: AvatarGroup
}), _defineProperty(_elementMappings, ElementName.OwnedByGroup, {
  component: AvatarGroup
}), _defineProperty(_elementMappings, ElementName.CreatedOn, {
  component: DateTime
}), _defineProperty(_elementMappings, ElementName.DueOn, {
  component: Lozenge
}), _defineProperty(_elementMappings, ElementName.LatestCommit, {
  component: Badge,
  props: {
    icon: IconType.Commit
  }
}), _defineProperty(_elementMappings, ElementName.LinkIcon, {
  component: Icon
}), _defineProperty(_elementMappings, ElementName.ModifiedBy, {
  component: Text
}), _defineProperty(_elementMappings, ElementName.ModifiedOn, {
  component: DateTime
}), _defineProperty(_elementMappings, ElementName.Preview, {
  component: Media
}), _defineProperty(_elementMappings, ElementName.Priority, {
  component: Badge
}), _defineProperty(_elementMappings, ElementName.ProgrammingLanguage, {
  component: Badge,
  props: {
    icon: IconType.ProgrammingLanguage
  }
}), _defineProperty(_elementMappings, ElementName.Provider, {
  component: Badge
}), _defineProperty(_elementMappings, ElementName.ReadTime, {
  component: Text
}), _defineProperty(_elementMappings, ElementName.SentOn, {
  component: DateTime
}), _defineProperty(_elementMappings, ElementName.SourceBranch, {
  component: Text
}), _defineProperty(_elementMappings, ElementName.State, {
  component: Lozenge
}), _defineProperty(_elementMappings, ElementName.SubscriberCount, {
  component: Badge,
  props: {
    icon: IconType.Subscriber
  }
}), _defineProperty(_elementMappings, ElementName.SubTasksProgress, {
  component: Badge,
  props: {
    icon: IconType.SubTasksProgress
  }
}), _defineProperty(_elementMappings, ElementName.StoryPoints, {
  component: AtlaskitBadge
}), _defineProperty(_elementMappings, ElementName.TargetBranch, {
  component: Text
}), _defineProperty(_elementMappings, ElementName.Title, {
  component: Link
}), _defineProperty(_elementMappings, ElementName.Location, {
  component: Link,
  props: {
    theme: SmartLinkInternalTheme.Grey
  }
}), _elementMappings);
var getContextKey = function getContextKey(name) {
  // Attempt to predict context prop name in advance to reduce the amount of
  // code run during runtime
  return name.length > 0 ? name.charAt(0).toLowerCase() + name.slice(1) : undefined;
};
var getData = function getData(elementName, contextKey, context) {
  if (!context) {
    return undefined;
  }
  var data = context[contextKey];
  switch (elementName) {
    case ElementName.AssignedToGroup:
    case ElementName.AuthorGroup:
    case ElementName.CollaboratorGroup:
    case ElementName.OwnedByGroup:
      var AvatarGroupsWithFallback = [ElementName.AssignedToGroup];
      var showFallbackAvatar = AvatarGroupsWithFallback.includes(elementName);
      return toAvatarGroupProps(data, showFallbackAvatar);
    case ElementName.AttachmentCount:
    case ElementName.ChecklistProgress:
    case ElementName.CommentCount:
    case ElementName.ViewCount:
    case ElementName.ReactCount:
    case ElementName.VoteCount:
    case ElementName.ProgrammingLanguage:
    case ElementName.SubscriberCount:
    case ElementName.LatestCommit:
    case ElementName.SubTasksProgress:
      return toBadgeProps(data);
    case ElementName.StoryPoints:
      return toAtlaskitBadgeProps(data);
    case ElementName.CreatedBy:
      return toFormattedTextProps(messages.created_by, context.createdBy);
    case ElementName.AssignedTo:
      return toFormattedTextProps(messages.assigned_to, context.assignedTo);
    case ElementName.OwnedBy:
      return toFormattedTextProps(messages.owned_by, context.ownedBy);
    case ElementName.CreatedOn:
      return toDateTimeProps('created', context.createdOn);
    case ElementName.DueOn:
      return toDateLozengeProps(context.dueOn);
    case ElementName.ModifiedBy:
      return toFormattedTextProps(messages.modified_by, context.modifiedBy);
    case ElementName.ModifiedOn:
      return toDateTimeProps('modified', context.modifiedOn);
    case ElementName.SentOn:
      return toDateTimeProps('sent', context.sentOn);
    case ElementName.ReadTime:
      return toFormattedTextProps(messages.read_time, data);
    case ElementName.SourceBranch:
    case ElementName.TargetBranch:
      return toTextProps(data);
    case ElementName.Title:
      return toLinkProps(context.title, context.url);
    default:
      return _typeof(data) === 'object' ? data : undefined;
  }
};
var toAvatarGroupProps = function toAvatarGroupProps(items, showFallbackAvatar) {
  return items ? {
    items: items
  } : showFallbackAvatar ? {
    items: []
  } : undefined;
};
var toBadgeProps = function toBadgeProps(label) {
  return label ? {
    label: label
  } : undefined;
};
var toAtlaskitBadgeProps = function toAtlaskitBadgeProps(value) {
  return value ? {
    value: value
  } : undefined;
};
var toDateLozengeProps = function toDateLozengeProps(dateString) {
  if (dateString) {
    var text = Date.parse(dateString) ? /*#__PURE__*/React.createElement(FormattedDate, {
      value: new Date(dateString),
      year: "numeric",
      month: "short",
      day: "numeric",
      formatMatcher: "best fit"
    }) : dateString;
    return {
      text: text
    };
  }
};
var toDateTimeProps = function toDateTimeProps(type, dateString) {
  return dateString ? {
    date: new Date(dateString),
    type: type
  } : undefined;
};
var toFormattedTextProps = function toFormattedTextProps(descriptor, context) {
  return context ? {
    message: {
      descriptor: descriptor,
      values: {
        context: context
      }
    }
  } : undefined;
};
var toLinkProps = function toLinkProps(text, url) {
  return text ? {
    text: text,
    url: url
  } : undefined;
};
var toTextProps = function toTextProps(content) {
  return content ? {
    content: content
  } : undefined;
};
export var createElement = function createElement(name) {
  var _ref = elementMappings[name] || {},
    BaseElement = _ref.component,
    props = _ref.props;
  var contextKey = getContextKey(name);
  if (!BaseElement) {
    throw Error("Element ".concat(name, " does not exist."));
  }
  return function (overrides) {
    var context = useContext(FlexibleUiContext);
    var data = getData(name, contextKey, context);
    return data ? /*#__PURE__*/React.createElement(BaseElement, _extends({}, props, data, overrides, {
      name: name
    })) : null;
  };
};